// Global Instructions Rule Applied!
//
// Fund full-form Capital Calls tab: the fund-scoped receipts + paid-status
// surface (payments are per-commitment and FIFO-attributed across calls, so
// the fund - not the individual call - is their natural home).
//
// Sections, all on the lightweight fetch-and-render recipe (no
// reporting-cfg machinery, CommitmentsTab precedent):
//   1. Position       - CallPositionView (shared).
//   2. Capital Calls  - the fund's calls; row link opens the issuance form.
//   3. Equalization   - the shared EqualizationSection component (also
//                       rendered on the consolidated fund's Capital Calls
//                       tab pointed at the primary fund).
//   4. Paid Status    - the FIFO grid from capital_calls__payment_status
//                       (per commitment x call; excess/prepaid rows have no
//                       call). Only Issued calls count as called.
//   5. Payments       - PaymentEntryGrid (shared): batch receipt entry, one
//                       row per Closed commitment. All-or-nothing server-side;
//                       it replaces the old one-LP-at-a-time Record Payment
//                       modal.
//   6. Contributions  - the payment history. Edit rewrites a booked receipt
//                       in place (same contribution and transaction ids);
//                       Delete trashes it. FIFO reflows either way. Both are
//                       disabled on a transfer-generated row, which has no
//                       receipt of its own.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, DownloadOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_CAPITAL_CALLS } from '../../../../../state_manager/globals.ts'
import {
  fetchCapitalCallsFiltered,
  fetchFundPaymentStatus,
  fetchFundContributions,
  deleteContribution
} from '../../../../capital_calls/services/capitalCallsService.ts'
import CallPositionView from '../../../../capital_calls/components/CallPositionView.tsx'
import EditContributionModal from '../../../../capital_calls/components/EditContributionModal.tsx'
import PaymentEntryGrid from '../../../../capital_calls/components/PaymentEntryGrid.tsx'
import EqualizationSection from '../../../../capital_calls/components/EqualizationSection.tsx'
import { exportJsonToExcel } from '../../../../../core/services/utilitiesService.ts'
import ExportRowsButton from '../../../../shared/table_grid_export/ExportRowsButton.tsx'
import type { CapitalCallRecord } from '../../../../../state_manager/data_models/capital_call_record.ts'
import type {
  FundContributionRow,
  FundPaymentStatusRow
} from '../../../../../state_manager/data_models/capital_call_detail.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const disp = (v: unknown): string => (v != null && v !== '' ? String(v) : '-')

interface CapitalCallsTabProps {
  fundId?: string | null
}

export default function CapitalCallsTab({ fundId }: CapitalCallsTabProps) {
  const { openFormInTab } = useTabManagerContext()
  const [modalApi, modalContextHolder] = Modal.useModal()

  const [calls, setCalls] = useState<CapitalCallRecord[]>([])
  const [paymentStatus, setPaymentStatus] = useState<FundPaymentStatusRow[]>([])
  const [contributions, setContributions] = useState<FundContributionRow[]>([])
  const [editingContributionId, setEditingContributionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const refetchAll = useCallback(async () => {
    if (!fundId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const [callsRes, statusRes, contribRes] = await Promise.all([
        fetchCapitalCallsFiltered([{ key: 'capital_calls__call_date', direction: 'desc' }], [], 1, 200, token, {
          fundId
        }),
        fetchFundPaymentStatus(fundId, token),
        fetchFundContributions(fundId, token)
      ])
      if (callsRes.error) showErrorToast(callsRes.error)
      if (statusRes.error) showErrorToast(statusRes.error)
      if (contribRes.error) showErrorToast(contribRes.error)
      setCalls(callsRes.data)
      setPaymentStatus(statusRes.data)
      setContributions(contribRes.data)
    } finally {
      setLoading(false)
    }
  }, [fundId])

  useEffect(() => {
    void refetchAll()
  }, [refetchAll])

  const handleDeleteContribution = useCallback(
    (row: FundContributionRow) => {
      modalApi.confirm({
        title: 'Delete this payment?',
        content: `${disp(row.investors__name)} — ${disp(row.capital_contributions__amount_fmt)} on ${disp(
          row.capital_contributions__payment_date_fmt
        )}. The booked receipt transaction is removed and the paid status reflows.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          const token = getAccessToken(store.getState().auth)
          const res = await deleteContribution(row.capital_contributions__id, token)
          if (res.error) {
            showErrorToast(res.error)
            return
          }
          toast.success('Payment deleted')
          await refetchAll()
        }
      })
    },
    [modalApi, refetchAll]
  )

  const exportFundName = useCallback(() => {
    const funds = (store.getState() as any).globalConfigs?.funds ?? []
    return funds.find((f: any) => f.id === fundId)?.name ?? 'fund'
  }, [fundId])

  const handleExportPaidStatus = useCallback(async () => {
    const token = getAccessToken(store.getState().auth)
    const res = await exportJsonToExcel(
      {
        data: paymentStatus.map((r) => ({
          Investor: r.investors__name ?? '',
          'Capital Call': r.capital_calls__id
            ? r.capital_calls__name || r.capital_calls__call_date_fmt || ''
            : 'Excess / prepaid',
          'Call Date': (r as any).capital_calls__call_date ?? '',
          Called: r.prog__called_amount ?? 0,
          Paid: r.prog__paid_amount ?? 0,
          Outstanding: r.prog__outstanding ?? 0
        })),
        columns: ['Investor', 'Capital Call', 'Call Date', 'Called', 'Paid', 'Outstanding'],
        filename: `paid_status_${exportFundName()}`,
        dateColumns: ['Call Date']
      },
      token
    )
    if (!res.success) showErrorToast(res.error || 'Export failed')
  }, [paymentStatus, exportFundName])

  const handleExportContributions = useCallback(async () => {
    const token = getAccessToken(store.getState().auth)
    const res = await exportJsonToExcel(
      {
        data: contributions.map((r) => ({
          Investor: r.investors__name ?? '',
          'Payment Date': (r as any).capital_contributions__payment_date ?? '',
          Amount: (r as any).capital_contributions__amount ?? 0,
          Receipt: (r as any).cfg_transaction_statuses__label ?? ''
        })),
        columns: ['Investor', 'Payment Date', 'Amount', 'Receipt'],
        filename: `contributions_${exportFundName()}`,
        dateColumns: ['Payment Date']
      },
      token
    )
    if (!res.success) showErrorToast(res.error || 'Export failed')
  }, [contributions, exportFundName])

  // ----- tables -----
  const callColumns = useMemo(
    () => [
      {
        title: 'Call Date',
        dataIndex: 'capital_calls__call_date_fmt',
        width: 110,
        render: (v: string, row: CapitalCallRecord) => (
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
            onClick={() =>
              openFormInTab(TABLE_CAPITAL_CALLS, row.capital_calls__id, row.capital_calls__name || 'Capital Call')
            }
          >
            {disp(v)}
          </button>
        )
      },
      { title: 'Name', dataIndex: 'capital_calls__name', render: disp },
      {
        title: 'Status',
        dataIndex: 'cfg_capital_call_statuses__label',
        width: 90,
        render: (v: string | null) =>
          v ? <Tag color={v === 'Issued' ? 'green' : 'blue'}>{v}</Tag> : '-'
      },
      {
        title: 'Amount',
        dataIndex: 'capital_calls__total_amount_fmt',
        width: 130,
        align: 'right' as const,
        render: disp
      },
      {
        title: 'Commitments',
        dataIndex: 'capital_calls__commitment_count',
        width: 110,
        align: 'right' as const,
        render: disp
      },
      {
        title: 'Booking',
        dataIndex: 'cfg_transaction_statuses__label',
        width: 90,
        render: (v: string | null) => (v ? <Tag color={v === 'Booked' ? 'green' : 'blue'}>{v}</Tag> : '-')
      }
    ],
    [openFormInTab]
  )

  const statusColumns = useMemo(
    () => [
      { title: 'Investor', dataIndex: 'investors__name', render: disp },
      {
        title: 'Capital Call',
        dataIndex: 'capital_calls__name',
        render: (_: unknown, row: FundPaymentStatusRow) =>
          row.capital_calls__id ? (
            disp(row.capital_calls__name || row.capital_calls__call_date_fmt)
          ) : (
            <Tag color='purple'>Excess / prepaid</Tag>
          )
      },
      { title: 'Call Date', dataIndex: 'capital_calls__call_date_fmt', width: 110, render: disp },
      {
        title: 'Called',
        dataIndex: 'prog__called_amount_fmt',
        width: 150,
        align: 'right' as const,
        // negative called = equalization credit to an early closer (Q2 of
        // the late-closer design) - style it as intentional, not an error
        render: (v: string | null, row: FundPaymentStatusRow) =>
          (row.prog__called_amount ?? 0) < 0 ? (
            <span>
              <Text type='success'>{disp(v)}</Text>
              <Tag color='purple' className='ml-1'>
                Equalization credit
              </Tag>
            </span>
          ) : (
            disp(v)
          )
      },
      { title: 'Paid', dataIndex: 'prog__paid_amount_fmt', width: 120, align: 'right' as const, render: disp },
      {
        title: 'Outstanding',
        dataIndex: 'prog__outstanding_fmt',
        width: 120,
        align: 'right' as const,
        render: (v: string | null, row: FundPaymentStatusRow) =>
          (row.prog__outstanding ?? 0) > 0 ? <Text type='danger'>{disp(v)}</Text> : disp(v)
      }
    ],
    []
  )

  const contributionColumns = useMemo(
    () => [
      { title: 'Investor', dataIndex: 'investors__name', render: disp },
      {
        title: 'Payment Date',
        dataIndex: 'capital_contributions__payment_date_fmt',
        width: 120,
        render: disp
      },
      {
        title: 'Amount',
        dataIndex: 'capital_contributions__amount_fmt',
        width: 130,
        align: 'right' as const,
        render: disp
      },
      {
        title: 'Receipt',
        dataIndex: 'cfg_transaction_statuses__label',
        width: 90,
        render: (v: string | null) => (v ? <Tag color={v === 'Booked' ? 'green' : 'blue'}>{v}</Tag> : '-')
      },
      {
        title: '',
        dataIndex: 'actions',
        width: 110,
        render: (_: unknown, row: FundContributionRow) => {
          // a transfer-generated contribution is booked by the intercompany
          // transfer leg, not by a receipt of its own - the server refuses
          // both actions on it, so offer neither
          const isTransfer = Boolean(row.capital_contributions__fund_group_transfer_id)
          const buttons = (
            <Space size={4}>
              <Button
                size='small'
                disabled={isTransfer}
                onClick={() => setEditingContributionId(row.capital_contributions__id)}
              >
                Edit
              </Button>
              <Button
                type='text'
                size='small'
                danger
                disabled={isTransfer}
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteContribution(row)}
              />
            </Space>
          )
          return isTransfer ? (
            <Tooltip title="Generated by an intercompany transfer — revert the transfer from the consolidated fund's Calls tab">
              <span>{buttons}</span>
            </Tooltip>
          ) : (
            buttons
          )
        }
      }
    ],
    [handleDeleteContribution]
  )

  if (!fundId) {
    return <div className='p-6 text-gray-500'>Save the fund first to manage capital calls.</div>
  }

  return (
    <div className='h-full overflow-y-auto p-3 flex flex-col gap-3'>
      {modalContextHolder}
      <EditContributionModal
        contributionId={editingContributionId}
        onClose={() => setEditingContributionId(null)}
        onSaved={() => void refetchAll()}
      />

      {fundId ? (
        <FormSection title='Position' titleColor='purple'>
          <CallPositionView fundId={fundId} />
        </FormSection>
      ) : null}

      <FormSection
        title='Capital Calls'
        titleColor='blue'
        loading={loading}
        headerActions={
          <Space>
            <ExportRowsButton
              name={`${exportFundName()} Capital Calls`}
              rows={calls}
              columns={[
                { title: 'Call Date', value: (r) => r.capital_calls__call_date, kind: 'date' },
                { title: 'Name', value: (r) => r.capital_calls__name },
                { title: 'Status', value: (r) => r.cfg_capital_call_statuses__label },
                { title: 'Amount', value: (r) => r.capital_calls__total_amount },
                { title: 'Currency', value: (r) => r.currencies__code },
                { title: 'Commitments', value: (r) => r.capital_calls__commitment_count },
                { title: 'Booking', value: (r) => r.cfg_transaction_statuses__label }
              ]}
            />
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              onClick={() =>
                openFormInTab(TABLE_CAPITAL_CALLS, null, 'New Capital Call', { fund: fundId })
              }
            >
              New Capital Call
            </Button>
            <Button size='small' icon={<ReloadOutlined />} onClick={() => void refetchAll()}>
              Refresh
            </Button>
          </Space>
        }
      >
        {calls.length === 0 ? (
          <Text type='secondary'>No capital calls for this fund yet.</Text>
        ) : (
          <Table
            size='small'
            columns={callColumns}
            dataSource={calls}
            pagination={false}
            rowKey='capital_calls__id'
          />
        )}
      </FormSection>

      <EqualizationSection fundId={fundId} onDraftCreated={() => void refetchAll()} />

      <FormSection
        title='Paid Status'
        titleColor='green'
        loading={loading}
        headerActions={
          <Button
            size='small'
            icon={<DownloadOutlined />}
            disabled={paymentStatus.length === 0}
            onClick={handleExportPaidStatus}
          >
            Export
          </Button>
        }
      >
        {paymentStatus.length === 0 ? (
          <Text type='secondary'>No issued calls or payments yet — only Issued calls count as called.</Text>
        ) : (
          <Table
            size='small'
            columns={statusColumns}
            dataSource={paymentStatus}
            pagination={false}
            rowKey={(row) => `${row.fund_commitments__id}-${row.capital_calls__id ?? 'excess'}`}
          />
        )}
      </FormSection>

      <PaymentEntryGrid fundId={fundId} onRecorded={() => void refetchAll()} />

      <FormSection
        title='Contributions'
        titleColor='purple'
        loading={loading}
        headerActions={
          <Space>
            <Button
              size='small'
              icon={<DownloadOutlined />}
              disabled={contributions.length === 0}
              onClick={handleExportContributions}
            >
              Export
            </Button>
          </Space>
        }
      >
        {contributions.length === 0 ? (
          <Text type='secondary'>No payments recorded yet.</Text>
        ) : (
          <Table
            size='small'
            columns={contributionColumns}
            dataSource={contributions}
            pagination={false}
            rowKey='capital_contributions__id'
          />
        )}
      </FormSection>

    </div>
  )
}
